import {
  CheckIcon,
  EyeIcon,
  ShieldCheckIcon,
  UserIcon,
  XIcon,
} from '@phosphor-icons/react';
import React, { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useRbac } from '@/hooks/use-rbac';
import {
  Permission,
  PERMISSION_DISPLAY_NAMES,
  RESOURCE_PERMISSIONS,
  User,
} from '@/types/sheets';

interface UserPermissionViewerProps {
  user: User;
  onClose?: () => void;
}

const PermissionMatrix: React.FC<{ permissions: Permission[] }> = ({
  permissions,
}) => {
  const allResources = Object.keys(RESOURCE_PERMISSIONS);
  const permissionMap = new Map<string, string[]>();

  permissions.forEach(permission => {
    permissionMap.set(permission.resource, permission.actions);
  });

  return (
    <div className='space-y-4'>
      {allResources.map(resource => {
        const userActions = permissionMap.get(resource) || [];
        const availableActions = RESOURCE_PERMISSIONS[resource];

        return (
          <Card key={resource} className='p-4'>
            <div className='flex items-center justify-between mb-3'>
              <h4 className='font-medium capitalize'>
                {resource.replace('_', ' ')}
              </h4>
              <Badge variant={userActions.length > 0 ? 'default' : 'outline'}>
                {userActions.length}/{availableActions.length} permissions
              </Badge>
            </div>

            <div className='grid grid-cols-2 md:grid-cols-5 gap-2'>
              {availableActions.map(action => {
                const hasPermission = userActions.includes(action);
                return (
                  <div
                    key={action}
                    className={`flex items-center justify-center p-2 rounded-md text-sm ${
                      hasPermission
                        ? 'bg-brand-10 text-brand-80 border border-brand-20'
                        : 'bg-gray-10 text-gray-5 border border-gray-20'
                    }`}
                  >
                    <div className='flex items-center space-x-1'>
                      {hasPermission ? (
                        <CheckIcon size={14} />
                      ) : (
                        <XIcon size={14} />
                      )}
                      <span className='capitalize'>
                        {PERMISSION_DISPLAY_NAMES[action] || action}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

const UserPermissionViewer: React.FC<UserPermissionViewerProps> = ({
  user,
}) => {
  const { getUserPermissions, loading } = useRbac();
  const [userPermissions, setUserPermissions] = useState<Permission[] | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const permissions = await getUserPermissions(user._id);
        setUserPermissions(permissions);
        setError(null);
      } catch (err) {
        setError('Failed to load user permissions');
      }
    };

    fetchPermissions();
  }, [user._id, getUserPermissions]);

  const getRoleName = () => {
    if (typeof user.role === 'string') {
      return user.role;
    }
    // Fallback for various role shapes
    return ((user as any).role?.name ??
      (user as any).role?.toString?.() ??
      '') as string;
  };

  const getPermissionSummary = () => {
    if (!userPermissions) return { total: 0, resources: 0 };

    const totalPermissions = userPermissions.reduce(
      (sum, permission) => sum + permission.actions.length,
      0
    );

    return {
      total: totalPermissions,
      resources: userPermissions.length,
    };
  };

  const summary = getPermissionSummary();

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center space-x-3'>
          <div className='h-12 w-12 rounded-full bg-brand-10 flex items-center justify-center'>
            <UserIcon className='h-6 w-6 text-brand-60' />
          </div>
          <div>
            <h2 className='text-xl font-semibold'>{user.username}</h2>
            <p className='text-gray-60'>{user.email}</p>
          </div>
        </div>
      </div>

      {/* User Info */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        <Card className='p-4'>
          <div className='flex items-center space-x-2 mb-2'>
            <ShieldCheckIcon size={20} className='text-brand-60' />
            <span className='font-medium'>Role</span>
          </div>
          <Badge className='bg-brand-60 text-white'>{getRoleName()}</Badge>
        </Card>

        <Card className='p-4'>
          <div className='flex items-center space-x-2 mb-2'>
            <EyeIcon size={20} className='text-orange-60' />
            <span className='font-medium'>Total Permissions</span>
          </div>
          <p className='text-2xl font-bold text-orange-60'>{summary.total}</p>
        </Card>

        <Card className='p-4'>
          <div className='flex items-center space-x-2 mb-2'>
            <CheckIcon size={20} className='text-pink-600' />
            <span className='font-medium'>Resources</span>
          </div>
          <p className='text-2xl font-bold text-pink-600'>
            {summary.resources}
          </p>
        </Card>
      </div>

      {/* Permissions Matrix */}
      <div>
        <h3 className='text-lg font-semibold mb-4'>Permission Details</h3>

        {loading.userPermissions && (
          <div className='space-y-4'>
            {[...Array(5)].map((_, i) => (
              <Card key={i} className='p-4'>
                <Skeleton className='h-6 w-32 mb-3' />
                <div className='grid grid-cols-5 gap-2'>
                  {[...Array(5)].map((_, j) => (
                    <Skeleton key={j} className='h-8 w-full' />
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}

        {error && (
          <Card className='p-8 text-center'>
            <p className='text-destructive-50'>{error}</p>
          </Card>
        )}

        {userPermissions && !loading.userPermissions && (
          <PermissionMatrix permissions={userPermissions} />
        )}
      </div>

      {/* Additional Information */}
      {user.lastLogin && (
        <Card className='p-4 bg-gray-5'>
          <p className='text-sm text-gray-60'>
            <strong>Last Login:</strong>{' '}
            {new Date(user.lastLogin).toLocaleString()}
          </p>
          <p className='text-sm text-gray-60 mt-1'>
            <strong>Account Status:</strong>
            <Badge
              variant={user.isActive ? 'default' : 'destructive'}
              className='ml-2'
            >
              {user.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </p>
        </Card>
      )}
    </div>
  );
};

export default UserPermissionViewer;
